<template>
  <div :class="wrapperClasses">
    <div class="chart-container">
      <canvas ref="chartCanvas"></canvas>
    </div>
    <div
      v-if="shouldRenderLegend"
      class="chart-legend-wrapper"
    >
      <div
        v-if="canToggleTrend"
        class="legend-actions"
      >
        <button
          type="button"
          class="legend-action-button"
          @click="toggleTrendVisibility"
        >
          {{ trendToggleLabel }}
        </button>
      </div>
      <div
        class="chart-legend"
        role="list"
      >
        <button
          v-for="item in legendItems"
          :key="item.index"
          type="button"
          class="legend-item"
          :class="{ 'legend-item--hidden': item.hidden }"
          @click="toggleLegendItem(item.index)"
          :aria-pressed="!item.hidden"
          role="listitem"
        >
          <span
            class="legend-swatch"
            :style="{ backgroundColor: item.color, borderColor: item.border }"
            aria-hidden="true"
          ></span>
          <span class="legend-label">{{ item.text }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)

export default {
  name: 'PlaytimeChart',
  props: {
    data: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: 'Playtime Over Time'
    },
    type: {
      type: String,
      default: 'line',
      validator: value => ['line', 'bar'].includes(value)
    },
    color: {
      type: String,
      default: '#667eea'
    },

    stackByGame: {
      type: Boolean,
      default: false
    },

    overlayLineData: {
      type: Array,
      default: () => []
    },
    overlayLineColor: {
      type: String,
      default: '#66c0f4'
    },
    overlayLineLabel: {
      type: String,
      default: 'Total Playtime'
    },

    dateRangeStart: {
      type: String,
      default: ''
    },
    dateRangeEnd: {
      type: String,
      default: ''
    },
    formatDateLabel: {
      type: Function,
      default: null
    },
    
    suspend: {
      type: Boolean,
      default: false
    },

    showLegend: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      chart: null,
      legendItems: [],
      lastChartData: null,
      trendVisible: false
    }
  },
  computed: {
    shouldRenderLegend() {
      return this.showLegend && this.legendItems.length > 0
    },
    wrapperClasses() {
      return [
        'chart-wrapper',
        this.shouldRenderLegend ? 'chart-wrapper--legend' : 'chart-wrapper--no-legend'
      ]
    },
    shouldDisplayTitle() {
      if (typeof this.title === 'string') {
        return this.title.trim().length > 0
      }
      return !!this.title
    },
    canToggleTrend() {
      if (!this.stackByGame || this.type !== 'bar') {
        return false
      }
      if (Array.isArray(this.overlayLineData) && this.overlayLineData.length > 0) {
        return true
      }
      return this.findOverlayDatasetIndex() >= 0
    },
    trendToggleLabel() {
      return this.trendVisible ? this.$t('charts.showBars') : this.$t('charts.showTrend')
    }
  },
  watch: {
    data: {
      handler() {
        this.updateChart()
      },
      deep: false
    },
    dateRangeStart() { this.updateChart() },
    dateRangeEnd() { this.updateChart() },
    '$i18n.locale'() { this.updateChart() },
    formatDateLabel() { this.updateChart() },
    stackByGame() { this.updateChart() },
    type() { this.recreateChart() },
    showLegend() { this.refreshLegendItems() },
    title() { this.updateChart() },
    overlayLineData: {
      handler() {
        this.updateChart()
      },
      deep: false
    },
    overlayLineColor() { this.updateChart() },
    overlayLineLabel() { this.updateChart() },
    suspend(newVal, oldVal) {
      
      if (oldVal === true && newVal === false) this.updateChart()
    }
  },
  mounted() {
    this.createChart()
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy()
    }
    this.legendItems = []
    this.lastChartData = null
  },
  methods: {
    createChart() {
      const ctx = this.$refs.chartCanvas
      this.chart = markRaw(new Chart(ctx, {
        type: this.type,
        data: this.getChartData(),
        options: this.getChartOptions()
      }))
      this.syncTrendVisibility()
      this.refreshLegendItems()
    },
    recreateChart() {
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
      this.legendItems = []
      this.lastChartData = null
      this.createChart()
    },
    updateChart() {
      if (this.suspend) return
      if (this.chart) {
        this.chart.data = this.getChartData()
        this.chart.options = this.getChartOptions()
        this.syncTrendVisibility()
        this.chart.update()
        this.refreshLegendItems()
      }
    },
    getChartData() {
      if (this.stackByGame && this.type === 'bar') {
        const items = Array.isArray(this.data) ? [...this.data] : []
        const sorted = items
          .map(entry => ({ ...entry, date: this.normalizeDateKey(entry.date) }))
          .filter(entry => entry.date)
          .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))

        if (sorted.length === 0) {
          const empty = { labels: [], datasets: [] }
          this.lastChartData = empty
          return empty
        }

        const normalizedLabels = []
        const labelSet = new Set()
        const byGame = new Map()
        const minBarLength = 4

        for (const entry of sorted) {
          if (!labelSet.has(entry.date)) {
            labelSet.add(entry.date)
            normalizedLabels.push(entry.date)
          }

          const gid = entry.app_id
          const name = entry.game_name || String(gid)
          const value = Number(entry.playtime_forever || entry.minutes || 0)

          if (!byGame.has(gid)) {
            byGame.set(gid, { name, values: new Map() })
          }

          const info = byGame.get(gid)
          info.values.set(entry.date, (info.values.get(entry.date) || 0) + value)
        }

        const labelKeys = this.generateRangeKeys(normalizedLabels)

        const datasets = []
        let index = 0
        for (const [gid, info] of byGame.entries()) {
          const data = labelKeys.map(label => {
            const raw = info.values.get(label)
            const numeric = Number(raw)
            return Number.isFinite(numeric) && numeric > 0 ? numeric : null
          })
        const color = this.colorForId(gid, index++)
        datasets.push({
          label: info.name,
          data,
          backgroundColor: color,
          borderColor: color,
          borderWidth: 1,
          minBarLength,
          order: 0,
          hidden: this.trendVisible
        })
      }

      const overlayDataset = this.buildOverlayDataset(labelKeys)
        if (overlayDataset) {
          datasets.push(overlayDataset)
        }

        const labels = labelKeys.map(label => this.formatLabel(label))
        const result = { labels, datasets }
        this.lastChartData = result
        return result
      }

      const items = Array.isArray(this.data) ? [...this.data] : []
      const sortedData = items
        .map(entry => ({ ...entry, date: this.normalizeDateKey(entry.date) }))
        .filter(entry => entry.date)
        .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))

      const totalsByDate = new Map()
      for (const item of sortedData) {
        const key = item.date
        if (!key) continue
        const numeric = Number(item.playtime_forever || 0)
        if (!Number.isFinite(numeric)) continue
        totalsByDate.set(key, (totalsByDate.get(key) || 0) + numeric)
      }

      const baseKeys = sortedData.map(item => item.date)
      const rangeKeys = this.generateRangeKeys(baseKeys)
      const labelKeys = rangeKeys.length ? rangeKeys : baseKeys
      const labels = labelKeys.map(label => this.formatLabel(label))

      if (labelKeys.length === 0) {
        const empty = { labels, datasets: [] }
        this.lastChartData = empty
        return empty
      }

      const result = {
        labels,
        datasets: [
          {
            label: 'Playtime',
            data: labelKeys.map(label => totalsByDate.get(label) || 0),
            borderColor: this.color,
            backgroundColor: this.color + '20',
            tension: 0.4,
            fill: true
          }
        ]
      }
      this.lastChartData = result
      return result
    },
    getChartOptions() {
      const styles = getComputedStyle(document.documentElement)
      const text = styles.getPropertyValue('--text')?.trim() || '#c7d5e0'
      const muted = styles.getPropertyValue('--muted')?.trim() || '#8f98a0'
      const grid = styles.getPropertyValue('--panel-border')?.trim() || 'rgba(102,192,244,0.1)'
      const bounds = this.computeYAxisBounds()
      const tooltipCallbacks = {
        label: context => {
          const value = this.valueFromContext(context)
          if (!Number.isFinite(value) || value <= 0) return null
          const formatted = this.formatPlaytimeValue(value)
          const label = context?.dataset?.label
          return label ? `${label}: ${formatted}` : formatted
        }
      }
      const tooltipConfig = { callbacks: tooltipCallbacks }

      if (this.stackByGame && this.type === 'bar') {
        tooltipConfig.filter = context => {
          const value = this.valueFromContext(context)
          return Number.isFinite(value) && value > 0
        }
      }

      const yTicks = {
        color: muted,
        callback: value => this.formatPlaytimeValue(value)
      }

      if (bounds) {
        yTicks.stepSize = bounds.stepSize
        yTicks.count = Math.round((bounds.max - bounds.min) / bounds.stepSize) + 1
      }

      const yAxis = {
        display: true,
        title: {
          display: true,
          text: this.$t('charts.playtimeAxis'),
          color: muted
        },
        beginAtZero: true,
        ticks: yTicks,
        grid: { color: grid },
        stacked: this.stackByGame && this.type === 'bar'
      }

      if (bounds) {
        yAxis.min = bounds.min
        yAxis.max = bounds.max
      }

      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: this.shouldDisplayTitle,
            text: this.title,
            font: {
              size: 16,
              weight: 'bold'
            },
            color: text
          },
          legend: {
            display: false
          },
          tooltip: tooltipConfig
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: this.$t('charts.dateAxis'),
              color: muted
            },
            ticks: { color: muted },
            grid: { color: grid },
            stacked: this.stackByGame && this.type === 'bar'
          },
          y: {
            ...yAxis
          }
        },
        interaction: {
          intersect: false,
          mode: 'index'
        }
      }
    },
    valueFromContext(context) {
      if (!context) return Number.NaN

      if (this.stackByGame && this.type === 'bar') {
        const raw = context.raw
        if (raw === null || raw === undefined) return Number.NaN
        if (typeof raw === 'number' && Number.isFinite(raw)) {
          return raw
        }
        if (typeof raw === 'object') {
          const candidate = typeof raw.y === 'number' ? raw.y : raw.x
          if (Number.isFinite(candidate)) {
            return candidate
          }
        }
        const numeric = Number(raw)
        return Number.isFinite(numeric) ? numeric : Number.NaN
      }

      const parsed = context.parsed
      if (typeof parsed === 'number' && Number.isFinite(parsed)) {
        return parsed
      }
      if (parsed && typeof parsed === 'object') {
        if (typeof parsed.y === 'number' && Number.isFinite(parsed.y)) {
          return parsed.y
        }
        if (typeof parsed.x === 'number' && Number.isFinite(parsed.x)) {
          return parsed.x
        }
      }
      const raw = context.raw
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        return raw
      }
      return Number.NaN
    },
    formatPlaytimeValue(value) {
      const numeric = Number(value)
      if (!Number.isFinite(numeric)) return this.$t('common.zeroMinutes')
      const rounded = Math.round(numeric)
      const sign = Math.sign(rounded)
      const total = Math.abs(rounded)
      const hours = Math.floor(total / 60)
      const minutes = total % 60

      let formatted
      if (hours > 0) {
        formatted = minutes > 0
          ? this.$t('common.hoursMinutes', { h: hours, m: minutes })
          : this.$t('common.hoursOnly', { h: hours })
      } else {
        formatted = this.$t('common.minutesOnly', { m: minutes })
      }

      if (sign < 0 && total > 0) {
        return `-${formatted}`
      }
      return formatted
    },
    refreshLegendItems() {
      if (!this.chart || !this.showLegend) {
        this.legendItems = []
        return
      }

      const datasets = Array.isArray(this.chart.data?.datasets)
        ? this.chart.data.datasets
        : []

      const items = []
      datasets.forEach((dataset, index) => {
        if (dataset?.overlay) {
          return
        }
        const visible = this.chart.isDatasetVisible(index)

        const rawBackground = Array.isArray(dataset.backgroundColor)
          ? dataset.backgroundColor[0]
          : dataset.backgroundColor

        const rawBorder = Array.isArray(dataset.borderColor)
          ? dataset.borderColor[0]
          : dataset.borderColor

        const background = !rawBackground || rawBackground === 'transparent'
          ? rawBorder || '#999'
          : rawBackground

        const border = rawBorder || background || '#999'

        items.push({
          index,
          text: dataset.label || `Series ${index + 1}`,
          color: background || border || '#999',
          border: border || background || '#999',
          hidden: !visible
        })
      })

      this.legendItems = items.filter(item => item.text)
    },
    toggleLegendItem(index) {
      if (!this.chart || !this.showLegend) return
      const isVisible = this.chart.isDatasetVisible(index)
      this.chart.setDatasetVisibility(index, !isVisible)
      if (this.isOverlayDataset(index)) {
        this.trendVisible = this.chart.isDatasetVisible(index)
        this.syncTrendVisibility()
      }
      this.chart.update()
      this.refreshLegendItems()
    },
    toggleTrendVisibility() {
      this.trendVisible = !this.trendVisible
      if (!this.chart) return
      this.syncTrendVisibility()
      this.chart.update()
      this.refreshLegendItems()
    },
    syncTrendVisibility() {
      if (!this.chart) return
      const datasets = Array.isArray(this.chart.data?.datasets)
        ? this.chart.data.datasets
        : []
      datasets.forEach((dataset, index) => {
        const isOverlay = !!dataset?.overlay
        const shouldShow = isOverlay ? this.trendVisible : !this.trendVisible
        this.chart.setDatasetVisibility(index, shouldShow)
        if (dataset) {
          dataset.hidden = !shouldShow
        }
      })
    },
    findOverlayDatasetIndex() {
      if (!this.chart) return -1
      const datasets = Array.isArray(this.chart.data?.datasets)
        ? this.chart.data.datasets
        : []
      return datasets.findIndex(dataset => dataset?.overlay === true)
    },
    isOverlayDataset(index) {
      if (!this.chart) return false
      const datasets = Array.isArray(this.chart.data?.datasets)
        ? this.chart.data.datasets
        : []
      const dataset = datasets[index]
      return !!dataset?.overlay
    },
    computeYAxisBounds() {
      const data = this.lastChartData
      if (!data || !Array.isArray(data.datasets) || data.datasets.length === 0) {
        return null
      }

      let values = []
      if (this.stackByGame && this.type === 'bar') {
        const totals = data.labels.map(() => 0)
        const lineValues = []
        data.datasets.forEach((dataset, index) => {
          if (dataset?.overlay) {
            if (!this.trendVisible) {
              return
            }
            const series = Array.isArray(dataset.data) ? dataset.data : []
            series.forEach(value => {
              const numeric = Number(value)
              if (Number.isFinite(numeric)) {
                lineValues.push(numeric)
              }
            })
            return
          }
          if (this.trendVisible) {
            return
          }
          if (this.chart && !this.chart.isDatasetVisible(index)) {
            return
          }
          const series = Array.isArray(dataset?.data) ? dataset.data : []
          series.forEach((value, index) => {
            const numeric = Number(value)
            if (Number.isFinite(numeric)) {
              totals[index] = (totals[index] || 0) + numeric
            }
          })
        })
        values = totals.concat(lineValues)
      } else {
        for (const dataset of data.datasets) {
          if (!dataset || !Array.isArray(dataset.data)) continue
          for (const value of dataset.data) {
            const numeric = Number(value)
            if (Number.isFinite(numeric)) {
              values.push(numeric)
            }
          }
        }
      }

      const filtered = values.filter(value => Number.isFinite(value))
      if (filtered.length === 0) {
        return null
      }

      const minValue = Math.min(...filtered, 0)
      const maxValue = Math.max(...filtered, 0)
      const baseMin = Math.min(minValue, 0)
      const baseMax = Math.max(maxValue, baseMin + 1)

      const intervalCount = 5
      const range = baseMax - baseMin
      const stepSize = Math.max(1, Math.ceil(range / intervalCount))
      const adjustedMin = Math.floor(baseMin / stepSize) * stepSize
      const adjustedMax = adjustedMin + stepSize * intervalCount

      return {
        min: adjustedMin,
        max: adjustedMax,
        stepSize
      }
    },
    colorForId(id, i) {
      
      const str = String(id)
      let hash = 0
      for (let c = 0; c < str.length; c++) hash = (hash << 5) - hash + str.charCodeAt(c)
      const hue = Math.abs(hash) % 360
      const sat = 65
      const light = 55
      return `hsl(${hue}, ${sat}%, ${light}%)`
    },
    formatLabel(value) {
      const key = this.normalizeDateKey(value)
      if (!key) return ''
      if (typeof this.formatDateLabel === 'function') {
        try {
          const formatted = this.formatDateLabel(key)
          if (formatted) {
            return formatted
          }
        } catch (error) {
          // Ignore formatting errors and fall back to the raw key
        }
      }
      return key
    },
    normalizeDateKey(value) {
      if (!value) return ''
      if (typeof value === 'string') {
        const trimmed = value.trim()
        return trimmed ? trimmed.slice(0, 10) : ''
      }

      const date = value instanceof Date ? value : new Date(value)
      if (!date || Number.isNaN(date.getTime())) return ''
      return date.toISOString().slice(0, 10)
    },
    generateRangeKeys(baseKeys = []) {
      const normalizedBase = Array.from(new Set(baseKeys
        .map(key => this.normalizeDateKey(key))
        .filter(Boolean)))
        .sort()

      const explicitStart = this.normalizeDateKey(this.dateRangeStart)
      const explicitEnd = this.normalizeDateKey(this.dateRangeEnd)

      let start = explicitStart || normalizedBase[0] || ''
      let end = explicitEnd || normalizedBase[normalizedBase.length - 1] || ''

      if (!start && !end) {
        return normalizedBase
      }

      if (!start) {
        start = end
      }
      if (!end) {
        end = start
      }

      if (start > end) {
        const temp = start
        start = end
        end = temp
      }

      const startDate = this.dateFromKey(start)
      const endDate = this.dateFromKey(end)

      if (!startDate || !endDate) {
        return normalizedBase
      }

      const labels = []
      const current = new Date(startDate.getTime())
      while (current <= endDate) {
        labels.push(current.toISOString().slice(0, 10))
        current.setUTCDate(current.getUTCDate() + 1)
      }
      return labels
    },
    dateFromKey(key) {
      if (!key) return null
      const str = String(key).trim()
      if (!str) return null
      const date = new Date(`${str}T00:00:00Z`)
      if (!date || Number.isNaN(date.getTime())) {
        return null
      }
      return date
    },
    buildOverlayDataset(labelKeys = []) {
      if (!Array.isArray(labelKeys) || labelKeys.length === 0) {
        return null
      }

      const entries = Array.isArray(this.overlayLineData) ? this.overlayLineData : []
      if (entries.length === 0) {
        return null
      }

      const totals = new Map()
      for (const entry of entries) {
        const key = this.normalizeDateKey(entry?.date)
        if (!key) continue
        const numeric = Number(entry?.playtime_forever ?? entry?.minutes ?? 0)
        if (!Number.isFinite(numeric)) continue
        totals.set(key, (totals.get(key) || 0) + numeric)
      }

      if (totals.size === 0) {
        return null
      }

      const color = this.overlayLineColor || '#66c0f4'

      return {
        type: 'line',
        label: this.overlayLineLabel || 'Total Playtime',
        data: labelKeys.map(label => totals.get(label) || 0),
        borderColor: color,
        backgroundColor: 'transparent',
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointRadius: 3,
        pointHoverRadius: 4,
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        order: 1,
        z: 10,
        overlay: true,
        hidden: !this.trendVisible
      }
    }
  }
}
</script>

<style scoped>
.chart-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chart-wrapper--legend {
  gap: 10px;
}

.chart-legend-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.legend-actions {
  display: flex;
  justify-content: flex-end;
}

.legend-action-button {
  border: 1px solid var(--panel-border);
  background: var(--panel);
  color: var(--text);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.legend-action-button:hover {
  background: rgba(102, 192, 244, 0.15);
}

.legend-action-button:focus {
  outline: 2px solid var(--accent, #66c0f4);
  outline-offset: 2px;
}

.chart-container {
  position: relative;
  flex: 1;
  min-height: 320px;
  max-height: 420px;
  width: 100%;
  background: var(--panel);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--panel-border);
}

.chart-wrapper--no-legend .chart-container {
  min-height: 360px;
  max-height: 440px;
}

.chart-container canvas {
  width: 100% !important;
  height: 100% !important;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 160px;
  overflow-y: auto;
  padding: 0 4px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--panel-border);
  border-radius: 6px;
  background: var(--panel);
  color: var(--text);
  padding: 6px 10px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.legend-item:focus {
  outline: 2px solid var(--accent, #66c0f4);
  outline-offset: 2px;
}

.legend-item--hidden {
  opacity: 0.4;
}

.legend-swatch {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  border: 1px solid currentColor;
  flex-shrink: 0;
}

.legend-label {
  /* Allow long game names to wrap inside the chip rather than forcing
     the chart card's min-content past the viewport on narrow screens.
     `overflow-wrap: anywhere` covers the "single very long word" case
     (CamelCase titles, no spaces) too. */
  word-break: break-word;
  overflow-wrap: anywhere;
  min-width: 0;
}

.legend-item {
  /* Cap a single chip at the legend container width so a 50-char title
     can never blow the parent flex sizing. */
  max-width: 100%;
}

@media (max-width: 768px) {
  .chart-container {
    min-height: 240px;
    max-height: 320px;
    padding: 12px;
  }

  .chart-legend {
    max-height: 140px;
  }

  .legend-actions {
    justify-content: flex-start;
  }

  .legend-action-button {
    width: 100%;
    text-align: center;
  }
}
</style> 
